// Trigger redeploy: minor comment for Vercel
import React, { useState, useEffect } from 'react';
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { Thermometer, Brain, Save, RotateCcw, Zap, Cpu, Bot, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AVAILABLE_MODELS } from '@/services/modelConfigService';

interface LLMSettings {
  llm_temperature: number;
  selected_model: string;
}

export default function LLMControlsTab() {
  const { toast } = useToast();
  const [settings, setSettings] = useState<LLMSettings>({
    llm_temperature: 0.2,
    selected_model: 'meta-llama/llama-4-maverick-17b-128e-instruct-fp8',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadLLMSettings();
  }, []);

  const loadLLMSettings = async () => {
    try {
      setIsLoading(true);
      
      // Fetch both temperature and model settings
      const { data, error } = await supabase
        .from('settings')
        .select('key, value')
        .in('key', ['llm_temperature', 'selected_model']);

      if (error) throw error;

      if (data && data.length > 0) {
        const settingsMap = data.reduce((acc, setting) => {
          if (setting.key === 'llm_temperature') {
            const temperature = parseFloat(setting.value);
            if (!isNaN(temperature)) {
              acc.llm_temperature = temperature;
            }
          } else if (setting.key === 'selected_model') {
            acc.selected_model = setting.value || 'meta-llama/llama-4-maverick-17b-128e-instruct-fp8';
          }
          return acc;
        }, {} as Partial<LLMSettings>);

        setSettings(prev => ({ ...prev, ...settingsMap }));
      }
    } catch (error: any) {
      console.error('Error loading LLM settings:', error);
      toast({
        title: "Error",
        description: "Failed to load LLM settings. Using defaults.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveLLMSettings = async () => {
    try {
      setIsSaving(true);

      // Get the selected model configuration
      const modelConfig = AVAILABLE_MODELS[settings.selected_model as keyof typeof AVAILABLE_MODELS];

      // Prepare settings to save
      const settingsToSave = [
        {
          key: 'llm_temperature',
          value: settings.llm_temperature.toString(),
          category: 'llm',
          description: 'Controls creativity vs determinism in AI responses',
          is_enabled: true,
        },
        {
          key: 'selected_model',
          value: settings.selected_model,
          category: 'llm',
          description: 'Currently selected LLM model for chat responses',
          is_enabled: true,
        },
        {
          key: 'current_api_key',
          value: modelConfig?.apiKey || '',
          category: 'llm',
          description: 'API key for the currently selected model',
          is_enabled: true,
        }
      ];

      // Save all settings
      for (const setting of settingsToSave) {
        const { error } = await supabase
          .from('settings')
          .upsert(
            {
              ...setting,
              last_updated: new Date().toISOString(),
            },
            {
              onConflict: 'key',
            }
          );

        if (error) throw error;
      }

      setHasChanges(false);
      toast({
        title: "Settings Saved",
        description: `Model switched to ${modelConfig?.displayName || 'selected model'}. Changes take effect immediately.`,
      });

      // Trigger a page reload to ensure all components pick up the new model
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error: any) {
      console.error('Error saving LLM settings:', error);
      toast({
        title: "Save Failed",
        description: "Failed to save LLM settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      llm_temperature: 0.2,
      selected_model: 'meta-llama/llama-4-maverick-17b-128e-instruct-fp8'
    });
    setHasChanges(true);
  };

  const updateTemperature = (value: number) => {
    setSettings(prev => ({ ...prev, llm_temperature: value }));
    setHasChanges(true);
  };

  const updateModel = (modelId: string) => {
    setSettings(prev => ({ ...prev, selected_model: modelId }));
    setHasChanges(true);
  };

  const getTemperatureDescription = (temp: number) => {
    if (temp <= 0.1) return "Ultra Conservative - Highly predictable, professional responses";
    if (temp <= 0.2) return "Very Conservative - Reliable and consistent responses";
    if (temp <= 0.3) return "Conservative - Balanced and professional responses";
    if (temp <= 0.5) return "Moderate - Some creativity while maintaining professionalism";
    if (temp <= 0.7) return "Creative - More varied and expressive responses";
    if (temp <= 0.9) return "Highly Creative - Very diverse responses";
    return "Maximum Creativity - Unpredictable and experimental responses";
  };

  const getTemperatureColor = (temp: number) => {
    if (temp <= 0.1) return "text-blue-500";
    if (temp <= 0.3) return "text-green-400";
    if (temp <= 0.5) return "text-yellow-400";
    if (temp <= 0.7) return "text-orange-400";
    return "text-red-400";
  };

  const getTemperatureIcon = (temp: number) => {
    if (temp <= 0.3) return "❄️";
    if (temp <= 0.5) return "🌡️";
    if (temp <= 0.7) return "🔥";
    return "⚡";
  };

  const selectedModelConfig = AVAILABLE_MODELS[settings.selected_model as keyof typeof AVAILABLE_MODELS];

  if (isLoading) {
    return (
      <div className="p-6 space-y-6 animate-fade-in">
        <div className="space-y-4">
          <div className="h-8 bg-slate-700/50 rounded-lg animate-pulse"></div>
          <div className="h-64 bg-slate-700/50 rounded-xl animate-pulse"></div>
          <div className="flex gap-3">
            <div className="h-10 bg-slate-700/50 rounded-lg animate-pulse flex-1"></div>
            <div className="h-10 bg-slate-700/50 rounded-lg animate-pulse flex-1"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 animate-fade-in">
      {/* Header Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/20 border border-blue-500/30">
            <Cpu className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-100">
              LLM Model & Temperature Controls
            </h2>
            <p className="text-sm text-slate-400">
              Select AI model and fine-tune response characteristics
            </p>
          </div>
        </div>
        <div className="w-16 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full opacity-60" />
      </div>

      {/* Model Selection */}
      <div className="bg-slate-800/50 border border-slate-600/50 rounded-xl backdrop-blur-sm">
        <div className="p-6 border-b border-slate-600/30">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-indigo-500/20 border border-indigo-500/30">
              <Bot className="h-4 w-4 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-slate-100">AI Model Selection</h3>
              <p className="text-sm text-slate-400">
                Choose the primary language model for chat responses
              </p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6">
          {/* Current Model Display */}
          {selectedModelConfig && (
            <div className="text-center p-6 bg-slate-700/30 rounded-lg border border-slate-600/30">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div>
                  <h4 className="text-xl font-semibold text-slate-100">{selectedModelConfig.name}</h4>
                  <p className="text-sm text-slate-400">{selectedModelConfig.provider}</p>
                </div>
                <Badge className={`${selectedModelConfig.badgeColor} text-white`}>
                  {selectedModelConfig.badge}
                </Badge>
              </div>
              <p className="text-slate-300 mb-4">{selectedModelConfig.description}</p>
            </div>
          )}

          {/* Model Selector */}
          <div className="space-y-3">
            <Label className="text-slate-300 font-medium">Select Model</Label>
            <Select value={settings.selected_model} onValueChange={updateModel}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-slate-100">
                <SelectValue placeholder="Choose a model" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                {Object.entries(AVAILABLE_MODELS).map(([modelId, config]) => (
                  <SelectItem 
                    key={modelId} 
                    value={modelId}
                    className="text-slate-100 focus:bg-slate-700 cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col">
                        <span className="font-medium">{config.displayName}</span>
                        <span className="text-xs text-slate-400">{config.provider}</span>
                      </div>
                      <Badge className={`${config.badgeColor} text-white text-xs`}>
                        {config.badge}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Temperature Control */}
      <div className="bg-slate-800/50 border border-slate-600/50 rounded-xl backdrop-blur-sm">
        <div className="p-6 border-b border-slate-600/30">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-purple-500/20 border border-purple-500/30">
              <Thermometer className="h-4 w-4 text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-slate-100">Response Temperature</h3>
              <p className="text-sm text-slate-400">
                Control creativity vs consistency in AI responses
              </p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6">
          {/* Current Temperature Display */}
          <div className="text-center">
            <div className={`text-6xl font-mono ${getTemperatureColor(settings.llm_temperature)} mb-2`}>
              {settings.llm_temperature.toFixed(2)}
            </div>
            <div className="text-2xl mb-2">
              {getTemperatureIcon(settings.llm_temperature)}
            </div>
            <div className={`text-lg font-medium ${getTemperatureColor(settings.llm_temperature)}`}>
              {getTemperatureDescription(settings.llm_temperature)}
            </div>
          </div>

          {/* Temperature Slider */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Slider
                value={[settings.llm_temperature]}
                onValueChange={(value) => updateTemperature(value[0])}
                max={1.0}
                min={0.0}
                step={0.05}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span className="flex items-center gap-1">❄️ <span className="hidden sm:inline">Deterministic</span></span>
                <span className="flex items-center gap-1">🌡️ <span className="hidden sm:inline">Balanced</span></span>
                <span className="flex items-center gap-1">⚡ <span className="hidden sm:inline">Creative</span></span>
              </div>
            </div>

            {/* Preset Buttons */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateTemperature(0.1)}
                className="border-blue-500/40 text-blue-300 hover:bg-blue-500/20 hover:border-blue-400/60 transition-all duration-200 h-auto py-3 flex flex-col gap-1"
              >
                <span className="text-xs font-medium">Ultra Safe</span>
                <span className="text-lg font-bold">0.1</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateTemperature(0.2)}
                className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 hover:border-emerald-400/60 transition-all duration-200 h-auto py-3 flex flex-col gap-1"
              >
                <span className="text-xs font-medium">Professional</span>
                <span className="text-lg font-bold">0.2</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateTemperature(0.5)}
                className="border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/20 hover:border-yellow-400/60 transition-all duration-200 h-auto py-3 flex flex-col gap-1"
              >
                <span className="text-xs font-medium">Balanced</span>
                <span className="text-lg font-bold">0.5</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateTemperature(0.7)}
                className="border-orange-500/40 text-orange-300 hover:bg-orange-500/20 hover:border-orange-400/60 transition-all duration-200 h-auto py-3 flex flex-col gap-1"
              >
                <span className="text-xs font-medium">Creative</span>
                <span className="text-lg font-bold">0.7</span>
              </Button>
            </div>
          </div>

          {/* Impact Preview */}
          <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-600/30">
            <h4 className="text-slate-100 font-medium mb-3 flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-emerald-500/20 border border-emerald-500/30">
                <Zap className="h-3.5 w-3.5 text-emerald-400" />
              </div>
              Expected Impact
            </h4>
            <div className="text-sm text-slate-300 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                <span><strong className="text-slate-200">Response Consistency:</strong> {settings.llm_temperature <= 0.3 ? 'Very High' : settings.llm_temperature <= 0.5 ? 'High' : settings.llm_temperature <= 0.7 ? 'Moderate' : 'Low'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
                <span><strong className="text-slate-200">Creative Variety:</strong> {settings.llm_temperature <= 0.3 ? 'Low' : settings.llm_temperature <= 0.5 ? 'Moderate' : settings.llm_temperature <= 0.7 ? 'High' : 'Very High'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                <span><strong className="text-slate-200">Professional Tone:</strong> {settings.llm_temperature <= 0.3 ? 'Guaranteed' : settings.llm_temperature <= 0.5 ? 'Likely' : settings.llm_temperature <= 0.7 ? 'Usually' : 'Variable'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 sm:justify-between pt-2">
        <Button 
          onClick={resetToDefaults}
          variant="outline"
          className="border-slate-600/50 text-slate-300 hover:bg-slate-700/50 hover:text-slate-100 transition-colors"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset to Defaults
        </Button>
        
        <Button 
          onClick={saveLLMSettings}
          disabled={!hasChanges || isSaving}
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Applying Changes...' : 'Apply & Restart'}
        </Button>
      </div>

      {/* Important Notice */}
      <div className="bg-amber-950/20 border border-amber-800/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-1.5 bg-amber-500/20 rounded-md">
            <Brain className="h-4 w-4 text-amber-400" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-200">
              Model Switch Notice
            </p>
            <p className="text-xs text-amber-300">
              Changing the model will automatically restart the application to ensure all components use the new configuration. 
              Active chats will be preserved but may take a moment to reload.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 